import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useApplePrice } from '@/common/hooks/apple-price';
import {
  abandonExpressCheckout,
  beginExpressCheckout,
  consumeExpressIntent,
  isAppleIapAvailable,
} from '@/common/lib/apple-iap';
import { playSound } from '@/common/lib/soundEffect';
import SelectPriceCard from '@/components/common/SelectPriceCard/SelectVipPriceCard';
import { ModalContext } from '@/components/Modals/Modal/config';
// import { transform } from 'framer-motion';
import { StringMain } from '@/components/Typography/Typography';
import { useAppSelector } from '@/redux/store';

import {
  BlueButton,
  CoralButton,
  MainButton,
  PurpleSmButton,
} from '../Button/Button';
import { TelegramStarIcon } from '../Icons';

import css from './style.module.scss';

import usdPng from '/img/icons/usd.png';
import tonsPerUsdImg from '/img/shop/tonsPerUsd.png';

interface props {
  onClick: (currency: 'TON' | 'XTR') => void;
  onUsdtBuy?: () => void;
  isLoading?: boolean;
  days?: number;
  price: number;
  starPrice: number;
  fullStarPrice?: number;
  discount?: number;
  classNames?: string;
  fullPrice?: number;
  mustBeUsdtButton?: boolean;
  variant?: 'default' | 'switch';
  disableTonButton?: boolean;
  usdPrice?: number;
  // VIP only: the App Store sells VIP at its own prices (subscriptions)
  appleVipDays?: number;
  // The list price the server bills in the App Store, when `price` already
  // has the balance (USDT) discount applied
  appleUsd?: number;
  // iOS app: start the App Store purchase as soon as the window opens (off
  // for windows the game opens by itself, without the player asking to buy)
  autoCheckout?: boolean;
}

const TONandStarsBuyButtons: React.FC<props> = ({
  onClick,
  onUsdtBuy,
  isLoading,
  price,
  starPrice,
  classNames,
  discount,
  fullPrice,
  mustBeUsdtButton,
  variant = 'default',
  usdPrice = price,
  appleVipDays,
  appleUsd,
  autoCheckout = true,
}) => {
  const { t } = useTranslation();
  const applePrice = useApplePrice(appleUsd ?? price, appleVipDays);
  const { closeModal, secondModal, thirdModal } = useContext(ModalContext);
  const tonsPerUsd = useAppSelector(
    (state) => state.user.extendedInfo?.exchangeRate.tonsPerUsd || 0,
  );
  const usdtBalance = useAppSelector(
    (state) => state.user.user?.balance.usdt || 0,
  );
  const [selected, setSelected] = useState(
    onUsdtBuy && mustBeUsdtButton ? 0 : 1,
  );
  const inactive: boolean = usdtBalance < usdPrice;
  // const calcBasePrice = (
  //   price: number,
  //   discount?: number,
  // ): ((value: number) => number) | undefined => {
  //   return discount
  //     ? transform([0, 100 - discount], [0, price], { clamp: false })
  //     : undefined;
  // };
  const selectCardClick = (item: number): void => {
    playSound('buttons/switch');
    setSelected(item);
  };
  const clickUsdt = (): void => {
    if (inactive || !onUsdtBuy) return;

    if (secondModal.isOpen) {
      thirdModal.openModal({
        renderKind: 'buyUsdtModal',
        props: { price: usdPrice, onUsdtBuy },
      });
    } else {
      secondModal.openModal({
        renderKind: 'buyUsdtModal',
        props: { price: usdPrice, onUsdtBuy },
      });
    }
  };

  const onClickSwitcherButton = (): void => {
    switch (selected) {
      case 0:
        clickUsdt();
        break;
      case 1:
        onClick('TON');
        break;
      case 2:
        onClick('XTR');
        break;
    }
  };

  const getPriceProps = (
    price: number,
    fullPrice?: number,
  ): { price: number; discountPrice?: number } => {
    if (!fullPrice) {
      return {
        price,
      };
    }

    return {
      price: fullPrice,
      discountPrice: price,
    };
  };

  const getUsdtPrice = (price: number): number =>
    tonsPerUsd ? Number((price * tonsPerUsd).toFixed(5)) : 0;

  // iOS app, express checkout: the purchase window stays hidden and the App
  // Store sheet opens right away. Paid → the window appears with its usual
  // pending/success state; cancelled or failed → it closes again.
  useEffect(() => {
    if (!isAppleIapAvailable() || !autoCheckout || !consumeExpressIntent()) {
      return;
    }

    beginExpressCheckout(() => {
      if (thirdModal.isOpen) thirdModal.closeModal();
      else if (secondModal.isOpen) secondModal.closeModal();
      else closeModal();
    });
    onClick('XTR');

    return abandonExpressCheckout;
  }, []);

  // iOS app: the App Store is the only way to pay — one button with its
  // price. It goes down the Stars path; the API layer and the openInvoice
  // bridge turn that into a StoreKit purchase (see common/lib/apple-iap).
  if (isAppleIapAvailable()) {
    return (
      <div className={cn('flex flex-col gap-4 w-full px-6', classNames)}>
        <MainButton
          text=""
          isLoading={isLoading}
          onClick={() => {
            playSound('buttons/switch');
            onClick('XTR');
          }}
        >
          <div className="flex items-center text-lg font-extrabold gap-1">
            {applePrice}
          </div>
        </MainButton>
      </div>
    );
  }

  if (variant === 'switch') {
    return (
      <div className={cn('flex flex-col gap-4 w-full px-6', classNames)}>
        <div className="grid grid-cols-3 gap-1">
          {onUsdtBuy && mustBeUsdtButton && (
            <SelectPriceCard
              onClick={() => selectCardClick(0)}
              active={selected === 0}
              label="Balance"
              type="usdt"
              variant="solid"
              sale={discount}
              {...getPriceProps(usdPrice, fullPrice)}
            />
          )}
          <SelectPriceCard
            onClick={() => selectCardClick(1)}
            active={selected === 1}
            label="Ton"
            type="ton"
            variant="solid"
            price={getUsdtPrice(price)}
          />
          <SelectPriceCard
            onClick={() => selectCardClick(2)}
            active={selected === 2}
            label="Stars"
            type="star"
            variant="solid"
            price={starPrice}
          />
        </div>

        <MainButton
          text={
            selected === 0
              ? inactive
                ? t('Balance: Not enough funds')
                : `$ ${usdPrice}`
              : ''
          }
          onClick={onClickSwitcherButton}
          {...(selected === 0 && { inactive: inactive, disabled: inactive })}
        >
          {selected !== 0 && (
            <div className="flex items-center text-lg font-extrabold gap-1">
              {selected === 1 ? (
                <img src={tonsPerUsdImg} className="size-5" />
              ) : (
                <TelegramStarIcon className="size-4 !text-gold" />
              )}
              {selected === 1 &&
                (tonsPerUsd ? (price * tonsPerUsd).toFixed(5) : 0)}
              {selected === 2 && starPrice}
            </div>
          )}
        </MainButton>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 px-[16px]">
      <div className={cn('flex w-full h-[48px] gap-[8px]', classNames)}>
        <BlueButton
          className="flex items-center h-full gap-1"
          onClick={() => onClick('TON')}
          isLoading={isLoading}
        >
          <img src={tonsPerUsdImg} width={24} alt="" />
          {/* {days !== 1 &&
          discount &&
          calcBasePrice(tonsPerUsd * price, discount) && (
            <StringSmall
              weight="medium"
              className={cn(
                '!text-white/40 relative text-crossed-out',
                css.discount,
              )}
            >
              {Number(
                calcBasePrice(tonsPerUsd * price, discount)!(100).toFixed(5),
              )}
            </StringSmall>
          )} */}
          <StringMain
            weight="extrabold"
            className={cn('!text-white/80', css.price)}
          >
            {tonsPerUsd ? (price * tonsPerUsd).toFixed(5) : 0}
          </StringMain>
        </BlueButton>
        <PurpleSmButton
          className={cn('flex items-center h-full gap-1')}
          onClick={() => onClick('XTR')}
          isLoading={isLoading}
        >
          <TelegramStarIcon className="w-6 h-6 shrink-0 !text-gold" />
          {/* {days !== 1 && discount && calcBasePrice(starPrice, discount) && (
          <StringSmall
            weight="medium"
            className={cn(
              '!text-white/40 relative text-crossed-out',
              css.discount,
            )}
          >
            {Math.round(Number(calcBasePrice(starPrice, discount)!(100)))}
          </StringSmall>
        )} */}
          <StringMain
            weight="extrabold"
            className={cn('!text-white/80', css.price)}
          >
            {starPrice}
          </StringMain>
        </PurpleSmButton>
      </div>
      {mustBeUsdtButton && onUsdtBuy && (
        <div className="relative" onClick={clickUsdt}>
          <CoralButton inactive={inactive} disabled={inactive}>
            <StringMain
              weight="extrabold"
              className={cn(
                '!text-white/80 flex items-center gap-1',
                css.price,
              )}
            >
              {inactive ? (
                <span>{t('Balance: Not enough funds')}</span>
              ) : (
                <>
                  <span>{t('Buy from balance')}</span>
                  <img src={usdPng} width={24} alt="" />
                  {!!discount && !!fullPrice && fullPrice !== price && (
                    <span className="!text-white/40 relative text-crossed-out !font-[500]">
                      {fullPrice}
                    </span>
                  )}
                  <span>{price}</span>
                </>
              )}
            </StringMain>
          </CoralButton>
        </div>
      )}
    </div>
  );
};

export default TONandStarsBuyButtons;
